import React from "react";
import { Axios } from "api/axios";
import { toast } from "react-toastify";

const toInputDate = (isoDate?: string) => {
  if (!isoDate) return "";
  const dateStr = isoDate.split("T")[0];
  return dateStr;
};

interface EditableDateCellProps {
  value?: string;
  canChange?: boolean;
  rowId: string;
  showExpiredBadge?: boolean;
  formatDisplay?: (val: string) => string;
  fieldName?: string; // Field name for API payload (default: "validity")
  preventPastDate?: boolean; // Whether to prevent selecting past dates (default: true)
  minDate?: string; // Minimum date that can be selected (ISO date string)
  successMessage?: string; // Custom success message
  errorMessage?: string; // Custom error message
  onSuccess?: () => void;
}

const EditableDateCell: React.FC<EditableDateCellProps> = ({
  value,
  canChange = false,
  rowId,
  showExpiredBadge = true,
  formatDisplay,
  fieldName = "validity",
  preventPastDate = true,
  minDate,
  successMessage,
  errorMessage,
  onSuccess,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [cellValue, setCellValue] = React.useState(toInputDate(value));
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    setCellValue(toInputDate(value));
  }, [value]);

  const handleSave = async () => {
    if (cellValue === toInputDate(value)) {
      setIsEditing(false);
      return;
    }
    if (!cellValue) {
      toast.error("Please select a valid date");
      setCellValue(toInputDate(value));
      setIsEditing(false);
      return;
    }

    // Check if the selected date is in the past (only if preventPastDate is true)
    if (preventPastDate && cellValue) {
      const selectedDate = new Date(cellValue);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        toast.error("Cannot select a date in the past");
        setCellValue(toInputDate(value));
        setIsEditing(false);
        return;
      }
    }

    // Check if the selected date is less than minDate
    if (minDate && cellValue) {
      const selectedDate = new Date(cellValue);
      const minDateObj = new Date(minDate);
      minDateObj.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);
      
      if (selectedDate < minDateObj) {
        toast.error("Cannot select a date earlier than the previous follow up date");
        setCellValue(toInputDate(value));
        setIsEditing(false);
        return;
      }
    }

    setIsSaving(true);
    setIsEditing(false);
    try {
      const payload: any = {};
      payload[fieldName] = cellValue || null;
      
      await Axios.patch(
        `api/quotation/current-status-validity-update/${rowId}/`,
        payload
      );

      toast.success(successMessage || `${fieldName === "validity" ? "Validity" : fieldName} date updated successfully!`);
      onSuccess?.();
    } catch (err: any) {
      // console.error("Failed to save validity:", err);
      setCellValue(toInputDate(value));
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        errorMessage ||
        `Failed to update ${fieldName === "validity" ? "validity" : fieldName} date`;
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") {
      setCellValue(toInputDate(value));
      setIsEditing(false);
    }
  };

  const expiryDate = cellValue ? new Date(cellValue) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isExpired = expiryDate ? expiryDate < today : false;

  const displayValue = formatDisplay
    ? formatDisplay(cellValue)
    : cellValue || "N/A";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        justifyContent: "space-between",
      }}
    >
      {!canChange ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "3px",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <span 
            style={{
              color: isExpired && showExpiredBadge ? "#dc3545" : "inherit",
              fontWeight: isExpired && showExpiredBadge ? 600 : "normal",
            }}
          >
            {displayValue}
          </span>
          {isExpired && showExpiredBadge && (
            <span
              style={{
                backgroundColor: "#dc3545",
                color: "white",
                padding: "2px 6px",
                borderRadius: "8px",
                fontSize: "0.7rem",
                fontWeight: 600,
              }}
            >
              Expired
            </span>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: "6px", width: "100%", justifyContent: "space-between" }}>
          {isEditing ? (
            <input
              type="date"
              value={cellValue}
              onChange={(e) => setCellValue(e.target.value)}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              autoFocus
              disabled={isSaving}
              min={
                minDate
                  ? toInputDate(minDate)
                  : preventPastDate
                  ? new Date().toISOString().split("T")[0]
                  : undefined
              }
              style={{
                padding: "2px 6px",
                borderRadius: 4,
                fontSize: "0.85rem",
                opacity: isSaving ? 0.6 : 1,
                cursor: isSaving ? "not-allowed" : "text",
                border: "1px solid #005D97",
                outline: "none",
              }}
            />
          ) : (
            <span
              onClick={() => !isSaving && canChange && setIsEditing(true)}
              style={{
                cursor: isSaving ? "wait" : canChange ? "pointer" : "default",
                opacity: isSaving ? 0.6 : 1,
                color: isExpired && showExpiredBadge ? "#dc3545" : "inherit",
                fontWeight: isExpired && showExpiredBadge ? 600 : "normal",
                padding: "2px 4px",
                borderRadius: "4px",
                transition: "all 0.2s ease",
                ...(canChange && {
                  "&:hover": {
                    backgroundColor: "#f8f9fa",
                  }
                })
              }}
              onMouseEnter={(e) => {
                if (canChange && !isSaving) {
                  e.currentTarget.style.backgroundColor = "#f8f9fa";
                }
              }}
              onMouseLeave={(e) => {
                if (canChange && !isSaving) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              {isSaving ? "Saving..." : displayValue}
            </span>
          )}
          {showExpiredBadge && isExpired && !isEditing && !isSaving && (
            <span
              style={{
                backgroundColor: "#dc3545",
                color: "white",
                padding: "2px 6px",
                borderRadius: "8px",
                fontSize: "0.7rem",
                fontWeight: 600,
              }}
            >
              Expired
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default EditableDateCell;